import { useAccountStorage, useOrganizationStorage } from '../../../../../../AppClients';
import { requireAnySystemPermission, requireOrgPermission } from '../../../../../../backend/features/permissions/shared';
import { useContactManagementTranslation, useUserState } from '../../../../../../ui/hooks';
import { handleErrorDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { MenuIconButton } from '@juvare/common-web-ui/MenuIconButton';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { showAlertSnackbar, showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { usePlatformLoader } from '@juvare/platform-sdk-react/ui/hooks';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';
import { showOrganizationTreeDialog } from '../../OrganizationsTreeDialog/OrganizationsTreeDialog';
import { getStatusAndRoleInNewOrganization, isUserOrgMember } from '../utils';
import { getDomainWithoutQueryParams } from '../utils/getDomainWithoutQueryParams';
export const OrganizationTreeItemActions = ({ item, isFocused, onReload }) => {
    const { t } = useContactManagementTranslation();
    const userState = useUserState();
    const organizationStorage = useOrganizationStorage();
    const accountStorage = useAccountStorage();
    const navigateContext = React.useContext(NavigationContext);
    const navigate = useNavigate();
    const { onStatelessReload } = usePlatformLoader({});
    const itemId = item.id.split('_')[0];
    const userCanSeeOrganization = item.visible ||
        !item.disabled ||
        requireAnySystemPermission(userState, 'view-invisible-organizations') ||
        requireOrgPermission(userState, itemId, 'manage-organization');
    const handleJoinOrganizationClick = (event) => {
        event.stopPropagation();
        showConfirmDialog({
            title: t('panels.organizations.joinOrganization', { name: item.name }),
            content: (React.createElement(Typography, { variant: 'body1', color: themeColors.text.subtle }, t('panels.organizations.confirmJoinOrg'))),
            confirmLabel: t('common.confirm'),
            onConfirm: () => {
                const accountOrgData = getStatusAndRoleInNewOrganization(userState, itemId);
                let snackbarMessage = t('panels.organizations.requestedToJoin', { name: item.name });
                if (requireAnySystemPermission(userState, 'manage-accounts')) {
                    snackbarMessage = t('panels.organizations.joinedOrg', { name: item.name });
                }
                accountStorage
                    .post({
                    action: 'partial-update',
                    data: accountOrgData,
                    id: userState.id,
                })
                    .then(() => {
                    showAlertSnackbar({
                        message: snackbarMessage,
                        type: 'success',
                    });
                    onStatelessReload().catch(handleErrorDialog);
                })
                    .catch(handleErrorDialog);
            },
        });
    };
    const handleCreateChildOrganizationClick = () => {
        navigateContext.navigate(() => {
            navigate({ pathname: `new/${itemId}` });
        });
    };
    const handleMoveOrganizationClick = () => {
        showOrganizationTreeDialog({
            multiSelect: false,
            showRootDirectory: true,
            onChange: (addedOrganizationIds) => {
                if (addedOrganizationIds.length) {
                    organizationStorage
                        .post({
                        action: 'partial-update',
                        id: itemId,
                        data: {},
                        parentId: addedOrganizationIds[0] === 'root' ? undefined : addedOrganizationIds[0],
                        updateParentId: true,
                    })
                        .then(() => {
                        showAlertSnackbar({ type: 'success', message: t('panels.organizations.moveOrg') });
                        onReload();
                    })
                        .catch((error) => {
                        if ((error === null || error === void 0 ? void 0 : error.type) === 'entity-circular-parent-reference') {
                            error.message = t('panels.organizations.movingRejected');
                        }
                        handleErrorDialog(error);
                    });
                }
            },
        });
    };
    const handleLeaveOrganizationClick = () => {
        showConfirmDialog({
            title: t('panels.organizations.leaveOrg'),
            content: React.createElement(Typography, null, t('panels.organizations.confirmLeaveOrg')),
            confirmLabel: t('common.confirm'),
            onConfirm: () => {
                const statuses = {};
                const orgRoleMap = Object.assign({}, userState.orgRole);
                userState.orgStatus.forEach((status) => {
                    const [orgId, userStatus] = status.split('_');
                    statuses[orgId] = userStatus;
                });
                statuses[itemId] = 'withdrawn';
                delete orgRoleMap[itemId];
                accountStorage
                    .post({
                    action: 'partial-update',
                    data: { orgStatus: statuses, orgRole: orgRoleMap },
                    id: userState.id,
                })
                    .then(() => {
                    showInfoSnackbar({
                        message: `Left ${item.name}`,
                        autoHideDuration: 2000,
                    });
                    onStatelessReload().catch(handleErrorDialog);
                })
                    .catch(handleErrorDialog);
            },
        });
    };
    const moreButtonMenuItems = [];
    if (requireOrgPermission(userState, itemId, 'manage-organization') || requireAnySystemPermission(userState, 'manage-organizations')) {
        moreButtonMenuItems.push({
            label: t('panels.organizations.createChildOrg'),
            onClick: handleCreateChildOrganizationClick,
            'data-test': 'create-child-organization-button',
        });
    }
    if (requireOrgPermission(userState, itemId, 'manage-organization') || requireAnySystemPermission(userState, 'manage-organizations')) {
        moreButtonMenuItems.push({
            label: t('panels.organizations.copyLink'),
            onClick: () => {
                const domain = getDomainWithoutQueryParams(window.location.href);
                const searchParams = new URLSearchParams();
                searchParams.set('id', itemId);
                navigator.clipboard
                    .writeText(`${domain}?${searchParams.toString()}`)
                    .then(() => {
                    showAlertSnackbar({
                        message: t('panels.organizations.saveToClipboardSuccess'),
                        type: 'success',
                        autoHideDuration: 2000,
                    });
                })
                    .catch((err) => {
                    handleErrorDialog(t('panels.organizations.saveToClipboardFailed'));
                    console.error(err);
                });
            },
            'data-test': 'copy-link-button',
        });
    }
    if (requireAnySystemPermission(userState, 'manage-organizations', 'all-organizations-admin')) {
        moreButtonMenuItems.push({
            label: t('panels.organizations.move'),
            onClick: handleMoveOrganizationClick,
            'data-test': 'move-button',
        });
    }
    if (isUserOrgMember(userState.orgStatus, itemId)) {
        moreButtonMenuItems.push({
            label: t('panels.organizations.leave'),
            onClick: handleLeaveOrganizationClick,
            'data-test': 'leave-button',
        });
    }
    return (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center' }, "aria-hidden": moreButtonMenuItems.length === 0 && !userCanSeeOrganization && isUserOrgMember(userState.orgStatus, itemId) },
        isUserOrgMember(userState.orgStatus, itemId) || !userCanSeeOrganization ? (React.createElement(Box, { sx: { flexGrow: 1, width: '62px' }, "aria-hidden": true })) : (React.createElement(Button, { sx: { flexGrow: 1 }, tabIndex: isFocused ? 0 : -1, onClick: handleJoinOrganizationClick, "data-test": 'join-organization-button' }, t('panels.organizations.join'))),
        moreButtonMenuItems.length > 0 && userCanSeeOrganization && (React.createElement(MenuIconButton, { sx: { flexGrow: 1 }, color: 'primary', tabIndex: isFocused ? 0 : -1, anchor: 'right', stopPropagationOnClick: true, menuItems: moreButtonMenuItems, "aria-label": t('common.more'), "data-test": 'more-actions-button' },
            React.createElement(MoreHoriz, null)))));
};
